import React from 'react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartProps } from '@app-types/visualization';
import sharedStyles from './charts.module.css';
import {
  coerceChartValues,
  formatAxisTick,
  formatTooltipValue,
  truncateCategoryLabel,
  type ChartValueFormat,
} from '../../utils/chartFormatters';

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];

interface CategoryTickProps {
  x?: number;
  y?: number;
  payload?: { value?: unknown };
}

/**
 * Category tick that truncates long labels instead of letting them run past the axis.
 * The untruncated value stays reachable through the native title tooltip on hover.
 */
const CategoryTick: React.FC<CategoryTickProps> = ({ x = 0, y = 0, payload }) => {
  const fullLabel = String(payload?.value ?? '');
  const shortLabel = truncateCategoryLabel(fullLabel);

  return (
    <g transform={`translate(${x},${y})`}>
      <title>{fullLabel}</title>
      <text
        dy={4}
        dx={-8}
        textAnchor="end"
        transform="rotate(-45)"
        fontSize={12}
        className={sharedStyles.categoryTickText}
      >
        {shortLabel}
      </text>
    </g>
  );
};

/**
 * A custom tick element opts out of Recharts' overlap-avoidance, so every category would
 * render regardless of available width. Only force all ticks while they still fit; past that
 * hand control back to Recharts so it thins them out instead of overlapping them.
 */
const MAX_FULLY_LABELLED_CATEGORIES = 12;

const resolveTickInterval = (categoryCount: number): 0 | 'preserveStartEnd' =>
  categoryCount <= MAX_FULLY_LABELLED_CATEGORIES ? 0 : 'preserveStartEnd';

const BarChart: React.FC<ChartProps> = ({
  data,
  config,
  colors = DEFAULT_COLORS,
  height = 350
}) => {
  if (!data || data.length === 0) {
    return <div className={sharedStyles.chartEmptyState}>No data available</div>;
  }

  // Get chart configuration
  const xAxisColumn = config?.xAxis?.column || Object.keys(data[0])[0];
  const primaryColumns = config?.yAxis?.primary?.columns || [];
  const primaryLabels = config?.yAxis?.primary?.labels || primaryColumns;
  const primaryFormat: ChartValueFormat = config?.yAxis?.primary?.format || 'number';

  // Fallback: auto-detect Y column if not configured
  const yColumn = primaryColumns[0] ||
    Object.keys(data[0]).find(k => k !== xAxisColumn && typeof data[0][k] === 'number') ||
    Object.keys(data[0])[1];

  // Aggregate SQL values can arrive as strings; Recharts needs numbers to build a numeric scale.
  const chartData = coerceChartValues(data, [yColumn]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey={xAxisColumn}
          interval={resolveTickInterval(chartData.length)}
          height={80}
          tick={<CategoryTick />}
        />
        <YAxis
          type="number"
          width={primaryFormat === 'currency' ? 80 : 60}
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => formatAxisTick(value, primaryFormat)}
        />
        <Tooltip
          formatter={(value) => formatTooltipValue(value, primaryFormat)}
        />
        <Bar
          dataKey={yColumn}
          fill={colors[0]}
          name={primaryLabels[0] || yColumn}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

export default BarChart;
